/**
 * Enlaces de invitación OPACOS.
 *
 * Antes el enlace era `…?join=r7&region=sa-east`: el id de sala a la vista, en
 * texto plano y adivinable. Con ids correlativos eso es una puerta abierta —
 * cualquiera puede probar `r1`, `r2`, `r3`… y colarse en salas ajenas sin que
 * nadie le haya invitado. Y si la sala tiene contraseña, había que mandarla
 * aparte, por otro canal, lo que en la práctica significa que no se usa.
 *
 * Aquí el enlace lleva UN SOLO parámetro (`?s=<token>`) que empaqueta sala,
 * región y contraseña en un blob ilegible de un vistazo.
 *
 * ## Qué es esto y qué NO es
 *
 * Es **ofuscación con integridad**, no criptografía: la clave viaja en el
 * cliente, así que quien se ponga a ello puede leer el token. Lo que consigue es
 * lo que hace falta para un enlace de invitación:
 *
 *  - **no se puede adivinar**: cambiar un carácter rompe la suma de control y el
 *    token se descarta, así que ya no se puede recorrer el espacio de ids;
 *  - **no se lee de un vistazo**: pegar el enlace en un chat público ya no
 *    enseña "sala r7" ni la contraseña;
 *  - **una sola cosa que copiar**: la contraseña viaja dentro.
 *
 * Lo que NO da: privacidad frente a quien tenga el enlace (obvio, es una
 * invitación) ni frente a alguien decidido a mirar el bundle. La autorización de
 * verdad la sigue haciendo el SERVIDOR, que valida sala y contraseña; esto solo
 * evita que el enlace sea una lista de la compra.
 */

/** Clave de ofuscación. No es un secreto: ver la nota de arriba. */
const KEY = "tak//attack/invite/1";

/** Prefijo de versión: si el formato cambia, los tokens viejos se reconocen. */
const V = "1";

export interface Invite {
  roomId: string;
  /** Región (edge) donde vive la sala; sin ella se caería en la de por defecto. */
  region?: string;
  /** Contraseña de la sala, si tiene. Viaja dentro del token, no en la URL. */
  password?: string;
}

/** XOR con la clave, repetida. Simétrico: la misma función cifra y descifra. */
function xor(bytes: Uint8Array): Uint8Array {
  const out = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) {
    out[i] = bytes[i] ^ KEY.charCodeAt(i % KEY.length);
  }
  return out;
}

/** Suma de control (FNV-1a de 32 bits) en base36. Detecta tokens manipulados. */
function checksum(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(36);
}

/** base64url sin relleno: cabe en una URL sin escapar nada. */
function toB64Url(bytes: Uint8Array): string {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromB64Url(token: string): Uint8Array | null {
  try {
    const b64 = token.replace(/-/g, "+").replace(/_/g, "/");
    const bin = atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4));
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
}

/**
 * Empaqueta una invitación en un token opaco.
 *
 * El payload es texto separado por barras (no JSON): el token acaba siendo la
 * mitad de largo, y un enlace de invitación se pega a mano en un chat.
 */
export function encodeInvite(inv: Invite): string {
  const payload = [inv.roomId, inv.region ?? "", inv.password ?? ""].join("|");
  const body = `${V}|${checksum(payload)}|${payload}`;
  const bytes = new TextEncoder().encode(body);
  return toB64Url(xor(bytes));
}

/**
 * Deshace `encodeInvite`. Devuelve `null` si el token está manipulado, truncado
 * o es de otra versión — que es exactamente lo que debe pasar con un enlace
 * inventado a mano.
 */
export function decodeInvite(token: string): Invite | null {
  const bytes = fromB64Url(token.trim());
  if (!bytes) return null;
  let body: string;
  try {
    body = new TextDecoder().decode(xor(bytes));
  } catch {
    return null;
  }
  const parts = body.split("|");
  if (parts.length < 5) return null;
  const [version, sum, roomId, region, ...rest] = parts;
  if (version !== V) return null;
  // La contraseña puede contener "|": se recompone el resto tal cual.
  const password = rest.join("|");
  if (checksum([roomId, region, password].join("|")) !== sum) return null;
  if (!roomId) return null;
  return {
    roomId,
    region: region || undefined,
    password: password || undefined,
  };
}

/**
 * Enlace completo de invitación para una sala.
 *
 * `base` es la URL del juego sin parámetros (`location.origin + pathname`).
 */
export function inviteUrl(base: string, inv: Invite): string {
  return `${base}?s=${encodeInvite(inv)}`;
}

/**
 * Lee la invitación de una URL. Entiende el token nuevo (`?s=`) y el formato
 * ANTIGUO en claro (`?join=&region=&pw=`), porque los enlaces que ya circulan
 * por ahí tienen que seguir funcionando: romperlos por mejorar el formato es
 * dejar tirada a la gente que ya había quedado para jugar.
 */
export function readInvite(params: URLSearchParams): Invite | null {
  const token = params.get("s");
  if (token) return decodeInvite(token);
  const roomId = params.get("join");
  if (!roomId) return null;
  return {
    roomId,
    region: params.get("region") ?? undefined,
    password: params.get("pw") ?? undefined,
  };
}
